/**
 * The single rule for which channel rows count as live, which need the owner, and which are
 * historical. Pure (no I/O) so the channels page (dashboard) and the owner overview (API) share it:
 * the overview must never list a channel the channels page does not show as needing attention.
 */

/** The fields the rule reads; the dashboard summary and the DB row both satisfy it. */
export interface ChannelVisibilityInput {
  readonly provider: string;
  readonly status: string;
  /** ISO string or Date; only its presence matters. */
  readonly disconnectedAt?: string | Date | null;
}

/**
 * `connected` = working; `attention` = needs the owner (failed, paused, a QR number waiting for a
 * scan); `inactive` = disconnected on purpose / migrated (not a problem).
 */
export type ChannelState = "connected" | "attention" | "inactive";

export function channelState(c: ChannelVisibilityInput): ChannelState {
  if (c.status === "connected") return "connected";
  if (c.status === "migrated") return "inactive";
  if (c.status === "disconnected") {
    // A QR session that never got scanned has no disconnect time: it is waiting for a scan.
    return c.provider === "whatsapp_waha" && !c.disconnectedAt ? "attention" : "inactive";
  }
  return "attention";
}

/** Disconnected / migrated rows are read-only history, not live channels. */
export function isChannelInactive(c: Pick<ChannelVisibilityInput, "status">): boolean {
  return c.status === "disconnected" || c.status === "migrated";
}

/**
 * Disconnected / migrated rows of a provider are hidden while the same provider has a live
 * channel (a reconnect creates a new row; the old one is a stale duplicate, not a channel).
 */
export function withoutStalePlaceholders<T extends ChannelVisibilityInput>(
  channels: readonly T[],
): T[] {
  const liveProviders = new Set(
    channels.filter((c) => !isChannelInactive(c)).map((c) => c.provider),
  );
  return channels.filter((c) => !isChannelInactive(c) || !liveProviders.has(c.provider));
}
